import i18n from '@/i18n';
import * as authUtils from '@/lib/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Error code definitions used throughout the system
 */
export enum ErrorCode {
  // General errors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',

  // Resource related errors
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',

  // Database related errors
  DATABASE_ERROR = 'DATABASE_ERROR',

  // Authentication related errors
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',

  // External service related errors
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',

  // Input data related errors
  INVALID_INPUT = 'INVALID_INPUT',

  // Date processing related errors
  DATE_PROCESSING_ERROR = 'DATE_PROCESSING_ERROR',

  // Network related errors
  NETWORK_ERROR = 'NETWORK_ERROR'
}

/**
 * Error response type definition
 */
export interface ErrorResponse {
  error: {
    code: ErrorCode;
    message: string;
    details?: unknown;
  };
}

/**
 * Class representing errors that occur during API communication
 */
export class ApiError extends Error {
  code: ErrorCode;
  status: number;
  details?: unknown;

  constructor(message: string, code: ErrorCode = ErrorCode.UNKNOWN_ERROR, status: number = 500, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * API request options type
 */
export type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  data?: unknown;
  params?: Record<string, string | number | undefined>;
};

/**
 * Get authentication header
 */
export const getAuthHeader = (): { Authorization: string } | Record<string, never> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Common API request function
 * Takes HTTP method, path, data, and query parameters to execute API requests with a unified interface
 */
export async function apiRequest<T>({ method = 'GET', path, data, params }: ApiOptions): Promise<T> {
  // Build query parameters
  const queryParams = params
    ? new URLSearchParams(
        Object.entries(params)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      ).toString()
    : '';

  const url = `${path}${queryParams ? `?${queryParams}` : ''}`;

  // Request configuration
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Add data to body if it exists
  if (data) {
    options.body = JSON.stringify(data);
  }

  try {
    // Execute request
    const response = await fetchWithAuth(url, options);

    // Process response
    return await handleApiResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.code === ErrorCode.AUTHENTICATION_ERROR || error.code === ErrorCode.AUTHORIZATION_ERROR) {
        authUtils.logout()
      }
    }
    throw error;
  }
}

/**
 * Helper function to process API responses
 * Parses successful responses as JSON and converts error responses to appropriate ApiError
 */
async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Handle 401, 403 errors as authentication errors
    if (response.status === 401 || response.status === 403) {
      throw new ApiError(
        i18n.t('common:errors.authentication'),
        ErrorCode.AUTHENTICATION_ERROR,
        response.status,
      );
    }

    try {
      const errorData = await response.json() as ErrorResponse;

      if (errorData.error) {
        throw new ApiError(
          errorData.error.message,
          errorData.error.code,
          response.status,
          errorData.error.details,
        );
      }
    } catch (e) {
      if (!(e instanceof ApiError)) {
        throw new ApiError(
          `${response.status} ${response.statusText}`,
          ErrorCode.NETWORK_ERROR,
          response.status,
        );
      }
      throw e;
    }

    throw new ApiError(
      `${response.status} ${response.statusText}`,
      ErrorCode.UNKNOWN_ERROR,
      response.status,
    );
  }

  return response.json();
}

/**
 * Function to execute HTTP requests with authentication information
 */
export const fetchWithAuth = async (
  path: string,
  options: RequestInit = {},
): Promise<Response> => {
  const url = `${API_URL}${path}`;
  const authHeader = getAuthHeader();

  const headers = {
    ...options.headers,
    ...authHeader,
  };

  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * Function to convert errors to user-friendly messages
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case ErrorCode.NETWORK_ERROR:
        return i18n.t('common:errors.network');
      case ErrorCode.AUTHENTICATION_ERROR:
        return i18n.t('common:errors.authentication');
      case ErrorCode.AUTHORIZATION_ERROR:
        return i18n.t('common:errors.authorization');
      case ErrorCode.RESOURCE_NOT_FOUND:
        return i18n.t('common:errors.resourceNotFound');
      case ErrorCode.VALIDATION_ERROR:
        return i18n.t('common:errors.validation');
      case ErrorCode.DATABASE_ERROR:
        return i18n.t('common:errors.database');
      case ErrorCode.DATE_PROCESSING_ERROR:
        return i18n.t('common:errors.dateProcessing');
      default:
        return error.message || i18n.t('common:errors.generic');
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return i18n.t('common:errors.unexpected');
}
